/**
 * Viewport embebido para Encapsulated PDF (estilo Stone).
 * No abre pestaña ni fuerza descarga al visualizar.
 */
import React, { Component } from 'react'
import * as pdfjs from 'pdfjs-dist/build/pdf'
import { extractEncapsulatedPdf } from '../utils/extractEncapsulatedPdf'
import './PdfViewport.css'

const WORKER_SRC = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`

try {
  if (pdfjs && pdfjs.GlobalWorkerOptions) {
    pdfjs.GlobalWorkerOptions.workerSrc = WORKER_SRC
  }
} catch (e) {
  /* ignore */
}

class PdfViewport extends Component {
  constructor(props) {
    super(props)
    this.canvasRef = React.createRef()
    this.wrapRef = React.createRef()
    this.pdfDoc = null
    this._loadGen = 0
    this.state = {
      page: 1,
      numPages: 0,
      scale: 1,
      fitMode: 'width',
      loading: true,
      error: null,
      docTitle: '',
    }
    this._pdfBytes = null
  }

  componentDidMount() {
    this.loadFromFiles(this.props.files)
    window.addEventListener('resize', this.handleResize)
  }

  componentDidUpdate(prevProps) {
    const prevKey = this.filesKey(prevProps.files)
    const nextKey = this.filesKey(this.props.files)
    if (prevKey !== nextKey) {
      this.loadFromFiles(this.props.files)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this._loadGen += 1
    this.destroyDoc()
  }

  filesKey = (files) => {
    const f = Array.isArray(files) && files[0]
    if (!f) return ''
    return String(
      f.sopInstanceUID ||
        f.imageId ||
        (f.data && f.data.byteLength) ||
        ''
    )
  }

  destroyDoc = () => {
    if (this.pdfDoc) {
      try {
        this.pdfDoc.destroy()
      } catch (e) {
        /* ignore */
      }
      this.pdfDoc = null
    }
  }

  resolvePart10Bytes = async (file) => {
    if (!file) throw new Error('Sin instancia PDF')
    if (file.data && file.data.byteLength) {
      return file.data instanceof Uint8Array
        ? file.data
        : new Uint8Array(file.data)
    }
    if (file.dicomUrl) {
      const res = await fetch(String(file.dicomUrl), { credentials: 'omit' })
      if (!res.ok) throw new Error(`HTTP ${res.status} al bajar PDF DICOM`)
      return new Uint8Array(await res.arrayBuffer())
    }
    throw new Error('No hay bytes Part-10 para el PDF encapsulado')
  }

  loadFromFiles = async (files) => {
    const gen = ++this._loadGen
    this.destroyDoc()
    this.setState({ loading: true, error: null, page: 1, numPages: 0 })
    try {
      const file = Array.isArray(files) && files[0]
      const part10 = await this.resolvePart10Bytes(file)
      if (gen !== this._loadGen) return
      const { bytes } = extractEncapsulatedPdf(part10)
      this._pdfBytes = bytes
      const loadingTask = pdfjs.getDocument({ data: bytes })
      const pdfDoc = await loadingTask.promise
      if (gen !== this._loadGen) {
        try {
          pdfDoc.destroy()
        } catch (e) {
          /* ignore */
        }
        return
      }
      this.pdfDoc = pdfDoc
      const title =
        (file &&
          file.series &&
          (file.series.seriesDescription || file.series.SeriesDescription)) ||
        (file && file.name) ||
        'Informe PDF'
      this.setState(
        {
          loading: false,
          error: null,
          numPages: pdfDoc.numPages || 1,
          page: 1,
          docTitle: title,
        },
        () => this.renderPage(1)
      )
    } catch (e) {
      if (gen !== this._loadGen) return
      this.setState({
        loading: false,
        error: (e && e.message) || 'No se pudo abrir el PDF',
        numPages: 0,
      })
    }
  }

  handleResize = () => {
    if (this.state.fitMode === 'width') {
      this.renderPage(this.state.page)
    }
  }

  computeFitScale = (page) => {
    const wrap = this.wrapRef.current
    if (!wrap || !page) return 1
    const viewport = page.getViewport({ scale: 1 })
    const avail = Math.max(40, wrap.clientWidth - 16)
    return avail / viewport.width
  }

  renderPage = async (pageNum) => {
    if (!this.pdfDoc) return
    const canvas = this.canvasRef.current
    if (!canvas) return
    try {
      const page = await this.pdfDoc.getPage(pageNum)
      let scale = this.state.scale
      if (this.state.fitMode === 'width') {
        scale = this.computeFitScale(page)
      }
      const viewport = page.getViewport({ scale })
      const ctx = canvas.getContext('2d')
      canvas.height = viewport.height
      canvas.width = viewport.width
      await page.render({ canvasContext: ctx, viewport }).promise
      if (this.state.fitMode === 'width' && scale !== this.state.scale) {
        this.setState({ scale })
      }
    } catch (e) {
      console.warn('PdfViewport renderPage', e)
    }
  }

  goPage = (delta) => {
    const next = Math.min(
      this.state.numPages,
      Math.max(1, this.state.page + delta)
    )
    if (next === this.state.page) return
    this.setState({ page: next }, () => this.renderPage(next))
  }

  zoomBy = (factor) => {
    const scale = Math.min(4, Math.max(0.25, this.state.scale * factor))
    this.setState({ scale, fitMode: 'manual' }, () =>
      this.renderPage(this.state.page)
    )
  }

  fitWidth = () => {
    this.setState({ fitMode: 'width' }, () => this.renderPage(this.state.page))
  }

  downloadPdf = () => {
    if (!this._pdfBytes) return
    const blob = new Blob([this._pdfBytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${String(this.state.docTitle || 'informe')
      .replace(/[^\w\-]+/g, '_')
      .slice(0, 64)}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 2000)
  }

  printPdf = () => {
    if (!this._pdfBytes) return
    const blob = new Blob([this._pdfBytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const w = window.open(url, '_blank', 'noopener,noreferrer')
    if (w) {
      const tryPrint = () => {
        try {
          w.focus()
          w.print()
        } catch (e) {
          /* ignore */
        }
      }
      setTimeout(tryPrint, 500)
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  }

  onWheel = (e) => {
    if (!this.state.numPages) return
    if (Math.abs(e.deltaY) < 4) return
    e.preventDefault()
    this.goPage(e.deltaY > 0 ? 1 : -1)
  }

  render() {
    const { page, numPages, scale, loading, error, docTitle } = this.state
    const active = !!this.props.active
    return (
      <div
        className={
          'udv-pdf-viewport' + (active ? ' udv-pdf-viewport--active' : '')
        }
        data-udv-pdf-viewport="true"
      >
        <div className="udv-pdf-toolbar" role="toolbar" aria-label="PDF">
          <span className="udv-pdf-title" title={docTitle}>
            {docTitle || 'PDF'}
          </span>
          <div className="udv-pdf-tools">
            <button
              type="button"
              className="udv-pdf-btn"
              disabled={page <= 1}
              onClick={() => this.goPage(-1)}
              title="Página anterior"
            >
              ‹
            </button>
            <span className="udv-pdf-page">
              {numPages ? `${page} / ${numPages}` : '—'}
            </span>
            <button
              type="button"
              className="udv-pdf-btn"
              disabled={!numPages || page >= numPages}
              onClick={() => this.goPage(1)}
              title="Página siguiente"
            >
              ›
            </button>
            <button
              type="button"
              className="udv-pdf-btn"
              onClick={() => this.zoomBy(1 / 1.2)}
              title="Alejar"
            >
              −
            </button>
            <span className="udv-pdf-zoom">{Math.round(scale * 100)}%</span>
            <button
              type="button"
              className="udv-pdf-btn"
              onClick={() => this.zoomBy(1.2)}
              title="Acercar"
            >
              +
            </button>
            <button
              type="button"
              className="udv-pdf-btn"
              onClick={this.fitWidth}
              title="Ajustar al ancho"
            >
              ↔
            </button>
            <button
              type="button"
              className="udv-pdf-btn"
              onClick={this.printPdf}
              title="Imprimir"
              disabled={!this._pdfBytes}
            >
              ⎙
            </button>
            <button
              type="button"
              className="udv-pdf-btn"
              onClick={this.downloadPdf}
              title="Descargar PDF"
              disabled={!this._pdfBytes}
            >
              ↓
            </button>
          </div>
        </div>
        <div
          className="udv-pdf-canvas-wrap"
          ref={this.wrapRef}
          onWheel={this.onWheel}
        >
          {loading && <div className="udv-pdf-msg">Cargando PDF…</div>}
          {error && <div className="udv-pdf-msg udv-pdf-msg--err">{error}</div>}
          <canvas
            ref={this.canvasRef}
            className="udv-pdf-canvas"
            style={{ display: loading || error ? 'none' : 'block' }}
          />
        </div>
      </div>
    )
  }
}

export default PdfViewport
